import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import FatturazioneContent from "../components/FatturazioneComponent";
import { cancelInvoice, createInvoicePayment, generateInvoiceXML, getAllInvoices, getInvoicePayments, printInvoiceCourtesyCopy, sendInvoiceBatch, updateInvoice } from "../APIs/InvoicesAPI";
import { useSnackbar } from "../contexts/SnackBarContext";
import { useTranslation } from "react-i18next";
import { SetupWSEventListener } from "../utils/setupWSEventListener";

function formatDate(dateString, locale) {
  if (!dateString) return "-";
  return new Intl.DateTimeFormat(locale || "it-IT").format(new Date(dateString));
}

function formatCurrency(value, locale) {
  return new Intl.NumberFormat(locale || "it-IT", {
    style: "currency",
    currency: "EUR",
  }).format(Number(value || 0));
}

function invoiceXmlFileName(invoice) {
  const baseName = invoice?.number || `fattura-${invoice?.id || "documento"}`;
  return `${String(baseName).replace(/[^a-zA-Z0-9._-]+/g, "_")}.xml`;
}

function downloadXmlFile(xml, fileName) {
  const blob = new Blob([xml], { type: "application/xml;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  anchor.style.display = "none";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function getNextDocumentNumber(documents, type) {
  const prefixMap = {
    FATTURA: "FT",
    DOCUMENTO_COMMERCIALE: "DC",
    CONTO_DIMOSTRATIVO: "CD",
    NOTA_CREDITO: "NC",
  };

  const prefix = prefixMap[type] || "FT";
  const currentYear = new Date().getFullYear();

  const sameTypeDocs = documents.filter((doc) => doc.type === type);

  let maxNumber = 0;

  sameTypeDocs.forEach((doc) => {
    const match = String(doc.number || "").match(/(\d{4})$/);
    if (match) {
      const numericPart = Number(match[1]);
      if (numericPart > maxNumber) {
        maxNumber = numericPart;
      }
    }
  });

  const nextNumber = String(maxNumber + 1).padStart(4, "0");
  return `${prefix}-${currentYear}-${nextNumber}`;
}

function mapInvoicePayloadToBillingRow(payload, existingDocuments) {
  // const source = payload?.data || payload;
  const source = payload;

  const tipoDocumentoMap = {
    "TD01": "FATTURA",
    "TD24": "FATTURA",
    "TD25": "FATTURA",
    "TD04": "NOTA_CREDITO",
  };

  const documentType = tipoDocumentoMap[source?.tipoDocumento] || "FATTURA";
  const taxableAmount = Number(source?.imponibile_importo || 0);
  const vatAmount = Number(source?.imposta || 0);
  const totalAmount = Number(
    source?.totaleDocumento || taxableAmount + vatAmount
  );
  const invoicePayments = Array.isArray(source?.payments) ? source.payments : [];
  const paidAmount = Number(
    invoicePayments.reduce((sum, payment) => sum + Number(payment.amount || 0), 0).toFixed(2)
  );
  const remainingAmount = Number(Math.max(0, totalAmount - paidAmount).toFixed(2));

  const statusMap = {
    BOZZA: "BOZZA",
    EMESSA: "EMESSO",
    EMESSO: "EMESSO",
    PAGATA: "PAGATO",
    PAGATO: "PAGATO",
    ANNULLATA: "ANNULLATO",
    ANNULLATO: "ANNULLATO",
    STORNATA: "STORNATO",
    STORNATO: "STORNATO",
    INVIO_IN_CORSO: "INVIO_IN_CORSO",
    INVIATA: "INVIATA",
    ERRORE: "ERRORE",
    ANNULLAMENTO_IN_CORSO: "ANNULLAMENTO_IN_CORSO",
  };

  const paymentMethodMap = {
    "MP01": "Contanti",
    "MP02": "Assegno",
    "MP05": "Bonifico",
    "MP08": "Carta",
    "CONTANTI": "Contanti",
    "CARTA": "Carta",
    "BONIFICO": "Bonifico",
  };

  const customerName =
    source?.denominazioneCliente ||
    source?.clienteNome ||
    source?.denominazione

  const vatOrFiscalCode =
    source?.partita_iva ||
    source?.codFiscaleCliente ||
    source?.partitaIVA

  const number =
    source?.numeroDocumento ||
    source?.numeroFattura ||
    getNextDocumentNumber(existingDocuments, documentType);

  const sent = source?.isSent;
  const notes = source?.isSent_notes;

  return {
    id: String(source?.id),
    type: documentType,
    tipoDocumento: source?.tipoDocumento,
    number,
    date: source?.createdAt
      ? new Date(source.createdAt).toISOString().slice(0, 10)
      : source?.dataDocumento
        ? new Date(source.dataDocumento).toISOString().slice(0, 10)
        : new Date().toISOString().slice(0, 10),
    customerName,
    vatOrFiscalCode,
    taxableAmount,
    vatAmount,
    totalAmount,
    paymentMethod:
      paymentMethodMap[source?.modalita_pagamento] ||
      source?.modalita_pagamento ||
      "-",
    status: statusMap[source?.statoFattura] || source?.statoFattura || "BOZZA",
    rawStatus: source?.statoFattura || "",
    incassoStatus: source?.incassoStatus || "NON_RISCOSSO",
    paymentDate: source?.incassoDate
      ? new Date(source.incassoDate).toISOString().slice(0, 10)
      : "",
    paidAmount,
    remainingAmount,
    invoicePayments,
    referenceDocument: source?.documentoRiferimento || "",
    reason: source?.causale || "",
    locationName: "Ristorante",
    tableName: "",
    operatorName: "Marco",
    isSent: Boolean(sent || source?.isSent),
    isSent_notes: notes || source?.isSent_notes,
    originalInvoiceId: source?.originalInvoiceId ? String(source.originalInvoiceId) : "",
    originalInvoiceNumber: source?.originalInvoice?.numeroFattura || "",
    creditNoteId: source?.creditNote?.id ? String(source.creditNote.id) : "",
    creditNoteNumber: source?.creditNote?.numeroFattura || "",
  };
}

export default function FatturazionePage() {
  const theme = useTheme();
  const { t } = useTranslation();
  const { showSnackbar } = useSnackbar();
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("ALL");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [billingDocuments, setBillingDocuments] = useState([]);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);

  const isDark = theme.palette.mode === "dark";
  const COLORS = useMemo(
    () => ({
      background: isDark ? theme.palette.background.default : "#F7F7F7",
      paper: isDark ? "#1E1E1E" : "#FFFFFF",
      border: isDark ? "#3A3A3A" : "#D8D8D8",
      title: isDark ? "#F5F5F5" : "#111111",
      subtitle: isDark ? "#B7B7B7" : "#5F5F5F",
      neutralText: isDark ? "#E6E6E6" : "#333333",
      neutralMuted: isDark ? "#AFAFAF" : "#666666",
      neutralHeaderBg: isDark ? "#242424" : "#FAFAFA",
      neutralTableHeadBg: isDark ? "#2A2A2A" : "#F4F4F4",
      neutralTableBodyBg: isDark ? "#333333" : "#FFFFFF",
      gold: "#A86A00",
      goldDark: "#8A5700",
      green: "#125605",
      greenBg: isDark ? "rgba(18,86,5,0.25)" : "#D8FFE5",
      red: "#A80000",
      redBg: isDark ? "rgba(168,0,0,0.20)" : "#F8E5E5",
      blue: "#0A4FAF",
      blueBg: isDark ? "rgba(10,79,175,0.22)" : "#E8F1FF",
      yellowBg: isDark ? "rgba(168,106,0,0.24)" : "#FFF3D8",
      rowBorder: isDark ? "#343434" : "#EFEFEF",
    }),
    [isDark, theme.palette.background.default]
  );

  const token = localStorage.getItem("accessToken");
  const tenant = localStorage.getItem("tenantId");

  const fetchData = useCallback(async () => {
    try {
      // Verifica se il token e il tenant sono disponibili
      if (!token || !tenant) {
        console.warn("Token o tenant non disponibili");
        showSnackbar(t("billing.errors.missingAuth"), "error");
        return;
      }
      const dataInvoices = await getAllInvoices(token, tenant);
      // setBillingDocuments(dataInvoices);
      const mapped = dataInvoices.map((invoice) =>
        mapInvoicePayloadToBillingRow(invoice, dataInvoices)
      );
      setBillingDocuments(mapped);
    } catch (err) {
      console.error("Errore recupero fatture:", err);
      showSnackbar(
        t("billing.errors.fetchInvoices") + ": " + err.message,
        "error"
      );
    }
  }, [token, tenant, showSnackbar, t]);

  // Fetch iniziale al mount, poi solo tramite WebSocket
  useEffect(() => {
    fetchData();
    const cleanup = SetupWSEventListener(fetchData);
    console.log("cleanup")
    return cleanup;
  }, [fetchData]);

  const handleIncassoStatusChange = async (id, newStatus) => {
    const previousRows = billingDocuments;
    const previousRow = billingDocuments.find((row) => row.id === id);
    const nextPaymentDate = newStatus === "RISCOSSO"
      ? previousRow?.paymentDate || new Date().toISOString().slice(0, 10)
      : "";

    setBillingDocuments((prev) =>
      prev.map((row) =>
        row.id === id
          ? {
            ...row,
            incassoStatus: newStatus,
            paymentDate: nextPaymentDate,
          }
          : row
      )
    );

    try {
      await updateInvoice(id, {
        incassoStatus: newStatus,
        incassoDate: nextPaymentDate || null,
      }, token, tenant);
      await fetchData();
    } catch (err) {
      setBillingDocuments(previousRows);
      showSnackbar(`Errore aggiornamento incasso: ${err.message}`, "error");
    }
  };

  const handlePaymentDateChange = async (id, newDate) => {
    const previousRows = billingDocuments;

    setBillingDocuments((prev) =>
      prev.map((row) =>
        row.id === id
          ? {
            ...row,
            paymentDate: newDate,
          }
          : row
      )
    );

    try {
      await updateInvoice(id, {
        incassoStatus: "RISCOSSO",
        incassoDate: newDate || null,
      }, token, tenant);
      await fetchData();
    } catch (err) {
      setBillingDocuments(previousRows);
      showSnackbar(`Errore aggiornamento data incasso: ${err.message}`, "error");
    }
  };

  const handleLoadInvoicePayments = async (invoiceId) => {
    return getInvoicePayments(invoiceId, token, tenant);
  };

  const handleCreateInvoicePayment = async (invoiceId, payload) => {
    const result = await createInvoicePayment(invoiceId, payload, token, tenant);
    showSnackbar("Incasso registrato", "success");
    await fetchData();
    return result;
  };

  const handleSendSelectedInvoices = async (invoiceIds) => {
    const ids = (invoiceIds || [])
      .map((id) => Number(id))
      .filter((id) => Number.isInteger(id) && id > 0);

    if (ids.length === 0) {
      showSnackbar("Seleziona almeno una fattura da inviare", "warning");
      return { results: [] };
    }

    try {
      const result = await sendInvoiceBatch(ids, token, tenant);
      const results = Array.isArray(result?.results) ? result.results : [];
      const sentCount = results.filter((item) => item.status === "SENT").length;
      const failedCount = results.filter((item) => item.status === "FAILED").length;
      const skippedCount = results.filter((item) => item.status === "SKIPPED").length;

      showSnackbar(
        `Invio completato: ${sentCount} inviate, ${failedCount} errori, ${skippedCount} saltate`,
        failedCount > 0 ? "warning" : "success"
      );
      await fetchData();
      return { results };
    } catch (err) {
      showSnackbar(`Errore invio fatture: ${err.message}`, "error");
      throw err;
    }
  };

  const handleExportSelectedInvoices = async (invoiceIds) => {
    const selectedIds = new Set((invoiceIds || []).map(String));
    const selectedInvoices = billingDocuments.filter((invoice) => selectedIds.has(String(invoice.id)));

    if (selectedInvoices.length === 0) {
      showSnackbar(t("billing.export.selectAtLeastOne", { defaultValue: "Seleziona almeno una fattura da esportare" }), "warning");
      return { failedIds: [] };
    }

    const results = await Promise.all(selectedInvoices.map(async (invoice) => {
      try {
        const xml = await generateInvoiceXML(invoice.id, token, tenant);
        downloadXmlFile(xml, invoiceXmlFileName(invoice));
        return { id: String(invoice.id), ok: true };
      } catch (error) {
        return { id: String(invoice.id), ok: false, error };
      }
    }));

    const successCount = results.filter((result) => result.ok).length;
    const failed = results.filter((result) => !result.ok);
    if (failed.length === 0) {
      showSnackbar(t("billing.export.success", {
        count: successCount,
        defaultValue: `${successCount} fatture esportate`,
      }), "success");
    } else {
      showSnackbar(t("billing.export.partialError", {
        success: successCount,
        failed: failed.length,
        defaultValue: `Esportate ${successCount} fatture, ${failed.length} non esportate`,
      }), successCount > 0 ? "warning" : "error");
    }

    return { failedIds: failed.map((result) => result.id) };
  };

  const handleCancelInvoice = async (invoiceId) => {
    try {
      const result = await cancelInvoice(invoiceId, token, tenant);
      if (result?.action === "DELETED") {
        showSnackbar("Bozza eliminata", "success");
      } else if (result?.action === "CREDIT_NOTE_CREATED") {
        showSnackbar(`Nota di credito ${result.invoice?.numeroFattura || ""} creata come bozza`, "success");
      } else if (result?.action === "CREDIT_NOTE_EXISTS") {
        showSnackbar(`Nota di credito gia presente: ${result.invoice?.numeroFattura || result.invoice?.id || ""}`, "info");
      } else {
        showSnackbar("Operazione completata", "success");
      }
      await fetchData();
      return result;
    } catch (err) {
      showSnackbar(`Errore annullamento fattura: ${err.message}`, "error");
      throw err;
    }
  };

  const handlePrintCourtesyCopy = async (invoiceId) => {
    try {
      await printInvoiceCourtesyCopy(invoiceId, token, tenant);
      showSnackbar("Stampa copia cortesia inviata", "success");
    } catch (err) {
      showSnackbar(`Errore stampa copia cortesia: ${err.message}`, "error");
    }
  };

  const handleOpenCreateDialog = () => {
    setOpenCreateDialog(true);
  };

  const handleCloseCreateDialog = () => {
    setOpenCreateDialog(false);
  };

  const handleInvoiceCreated = (invoicePayload) => {
    const newRow = mapInvoicePayloadToBillingRow(
      invoicePayload,
      billingDocuments
    );

    setBillingDocuments((prev) => [newRow, ...prev]);
    setOpenCreateDialog(false);
  };

  const filteredRows = useMemo(() => {
    return billingDocuments.filter((row) => {
      const text = search.trim().toLowerCase();

      const matchesSearch =
        !text ||
        String(row.number || "").toLowerCase().includes(text) ||
        String(row.customerName || "").toLowerCase().includes(text) ||
        String(row.vatOrFiscalCode || "").toLowerCase().includes(text) ||
        String(row.locationName || "").toLowerCase().includes(text) ||
        String(row.status || "").toLowerCase().includes(text) ||
        String(row.rawStatus || "").toLowerCase().includes(text) ||
        String(row.isSent || "").toLowerCase().includes(text) ||
        String(row.isSent_notes || "").toLowerCase().includes(text);

      const matchesType = typeFilter === "ALL" || row.type === typeFilter;
      const matchesStatus = statusFilter === "ALL" || row.status === statusFilter;

      const rowDate = row.date ? new Date(row.date) : null;
      const fromOk = !dateFrom || (rowDate && rowDate >= new Date(dateFrom));
      const toOk = !dateTo || (rowDate && rowDate <= new Date(dateTo));

      return matchesSearch && matchesType && matchesStatus && fromOk && toOk;
    });
  }, [billingDocuments, search, typeFilter, statusFilter, dateFrom, dateTo]);

  const summary = useMemo(() => {
    const totalDocuments = billingDocuments.length;
    const totalInvoices = billingDocuments.filter((x) => x.type === "FATTURA").length;
    const totalCommercial = billingDocuments.filter(
      (x) => x.type === "DOCUMENTO_COMMERCIALE"
    ).length;
    const totalCreditNotes = billingDocuments.filter(
      (x) => x.type === "NOTA_CREDITO"
    ).length;
    const totalAmount = billingDocuments.reduce(
      (sum, item) => sum + Number(item.totalAmount || 0),
      0
    );

    return {
      totalDocuments,
      totalInvoices,
      totalCommercial,
      totalCreditNotes,
      totalAmount,
    };
  }, [billingDocuments]);

  return (
    <Box
      className="admin-responsive-page"
      sx={{
        minHeight: "100vh",
        background: COLORS.background,
        p: { xs: 2, md: 3 },
      }}
    >
      <FatturazioneContent
        colors={COLORS}
        summary={summary}
        rows={filteredRows}
        search={search}
        setSearch={setSearch}
        typeFilter={typeFilter}
        setTypeFilter={setTypeFilter}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        dateFrom={dateFrom}
        setDateFrom={setDateFrom}
        dateTo={dateTo}
        setDateTo={setDateTo}
        formatCurrency={formatCurrency}
        formatDate={formatDate}
        onIncassoStatusChange={handleIncassoStatusChange}
        onPaymentDateChange={handlePaymentDateChange}
        onLoadInvoicePayments={handleLoadInvoicePayments}
        onCreateInvoicePayment={handleCreateInvoicePayment}
        onSendSelectedInvoices={handleSendSelectedInvoices}
        onExportSelectedInvoices={handleExportSelectedInvoices}
        onCancelInvoice={handleCancelInvoice}
        onPrintCourtesyCopy={handlePrintCourtesyCopy}
        onOpenCreateDialog={handleOpenCreateDialog}
        openCreateDialog={openCreateDialog}
        onCloseCreateDialog={handleCloseCreateDialog}
        onInvoiceCreated={handleInvoiceCreated}
      />
    </Box>
  );
}
